import axios from 'axios';
import clearApiCache from '@/lib/clearApiCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling.
//
// A 401 during a signed-in session means the token is dead and the
// user needs to log in again — so clear storage and send them to
// /login. But a 401 from an auth endpoint (wrong password, bad OTP,
// expired reset code) is the caller's error to show in a toast; we
// must NOT redirect, or the LoginPage self-refreshes, the toast is
// torn down, and the user just sees a blank form.
//
// Same reason we skip the redirect when we're already on /login or
// /register: there's nowhere to send them.
const AUTH_ENDPOINT_PREFIXES = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-signup-otp',
  '/auth/password-reset/',   // added by PR 14; harmless until then
];

function _isAuthEndpoint(url) {
  if (!url) return false;
  // url may be absolute (https://devapi.…/api/auth/login) or relative
  // (`/auth/login`). Match the suffix.
  return AUTH_ENDPOINT_PREFIXES.some((p) => url.includes(p));
}

function _onAuthPage() {
  if (typeof window === 'undefined') return false;
  const path = window.location?.pathname || '';
  return path === '/login' || path === '/register' || path.startsWith('/auth');
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const authFlow = _isAuthEndpoint(url) || _onAuthPage();
      if (!authFlow) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearApiCache();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  // Register with phone + password + name + user_type. Backend creates
  // the user and starts a Twilio Verify signup OTP.
  register: (data) => api.post('/auth/register', data),
  // Login is phone + password. Returns tokens directly, no OTP step.
  login: (data) => api.post('/auth/login', data),
  // Verify the signup OTP (Twilio) — returns tokens on success.
  verifySignupOTP: (data) => api.post('/auth/verify-signup-otp', data),
  // Password reset — mirrors signup's shape (recaptcha on request,
  // phone + otp + new password on confirm). Response from `request` is
  // deliberately generic to avoid user enumeration.
  requestPasswordReset: (data) => api.post('/auth/password-reset/request', data),
  confirmPasswordReset: (data) => api.post('/auth/password-reset/confirm', data),
  logout: () => api.post('/auth/logout'),
};

// User APIs
export const userAPI = {
  getUsers: (userType) => api.get(`/users${userType ? `?user_type=${userType}` : ''}`),
  getAnalytics: (userId) => api.get(`/analytics/${userId}`),
  update: (userId, data) => api.patch(`/users/${userId}`, data),
};

// Farm APIs
export const farmAPI = {
  getPlots: (userId) => api.get(`/plots/user/${userId}`),
  createPlot: (data) => api.post('/plots', data),
  getPlot: (plotId) => api.get(`/plots/${plotId}`),
  updatePlot: (plotId, data) => api.patch(`/plots/${plotId}`, data),
  deletePlot: (plotId) => api.delete(`/plots/${plotId}`),
  diagnosisConfig: () => api.get('/diagnosis/config'),
  createDiagnosis: (data) => api.post('/diagnosis', data),
  getDiagnoses: (userId) => api.get(`/diagnosis/user/${userId}`),
  createNutritionPlan: (data) => api.post('/nutrition/plan', data),
  getNutritionPlan: (plotId) => api.get(`/nutrition/plot/${plotId}`),
  createSprayTask: (data) => api.post('/spray/schedule', data),
  getSprayTasks: (userId) => api.get(`/spray/schedule/user/${userId}`),
  updateSprayTaskStatus: (taskId, status) => api.patch(`/spray/schedule/${taskId}/status`, status),
};

// Spray plans: built from the product the farmer picks.
export const sprayAPI = {
  products: (q) => api.get('/spray/products', { params: { q } }),
  plans: () => api.get('/spray/plans'),
  makePlan: (data) => api.post('/spray/plans', data),
  markTask: (taskId, done) => api.patch(`/spray/tasks/${taskId}`, { done }),
  deletePlan: (planId) => api.delete(`/spray/plans/${planId}`),
};

// Weather APIs
export const weatherAPI = {
  getWeather: (lat, lon) => api.get(`/weather?latitude=${lat}&longitude=${lon}`),
  getForecast: (lat, lon, days = 7) => api.get(`/weather/forecast?latitude=${lat}&longitude=${lon}&days=${days}`),
};

// Product APIs
export const productAPI = {
  browse: (params) => api.get('/products/browse', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getManufacturerProducts: (manufacturerId) => api.get(`/manufacturer/products/${manufacturerId}`),
  getManufacturerProductDetail: (productId) => api.get(`/manufacturer/products/detail/${productId}`),
  createProduct: (data, manufacturerId) => api.post(`/manufacturer/products?manufacturer_id=${manufacturerId}`, data),
  updateProduct: (productId, data) => api.put(`/manufacturer/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/manufacturer/products/${productId}`),
};

// Relationship APIs
export const relationshipAPI = {
  invite: (data) => api.post('/relationships/invite', data),
  getPending: (userId) => api.get(`/relationships/pending/${userId}`),
  action: (relationshipId, action) => api.put(`/relationships/${relationshipId}/action`, { action }),
  getNetwork: (userId) => api.get(`/relationships/network/${userId}`),
  delete: (relationshipId) => api.delete(`/relationships/${relationshipId}`),
  searchByPhone: (phone) => api.get(`/relationships/search/${phone}`),
  getAvailableDistributors: (manufacturerId) => api.get(`/relationships/available-distributors/${manufacturerId}`),
  // direction 'downstream' (default): connections I started (deal targets).
  // direction 'any': accepted connections started by either side.
  getConnectedUsers: (userId, { direction } = {}) =>
    api.get(`/relationships/connected-users/${userId}`, { params: direction ? { direction } : undefined }),
  getAccessibleProducts: (distributorId) => api.get(`/products/accessible/${distributorId}`),
  getAccessibleInventory: (shopkeeperId) => api.get(`/inventory/accessible/${shopkeeperId}`),
};

// Inventory APIs
export const inventoryAPI = {
  get: (distributorId) => api.get(`/inventory/${distributorId}`),
  add: (distributorId, data) => api.post(`/inventory/${distributorId}/add`, data),
  update: (inventoryId, data) => api.put(`/inventory/${inventoryId}`, data),
  getLowStock: (distributorId) => api.get(`/inventory/${distributorId}/low-stock`),
};

// NAFDAC master list and "my products" (distributors and shops)
export const masterAPI = {
  search: (params) => api.get('/master-products', { params }),
  addFromList: (productIds) => api.post('/my-products/from-master', { product_ids: productIds }),
  addManual: (data) => api.post('/my-products', data),
  upload: (filename, contentBase64, commit) =>
    api.post('/my-products/upload', { filename, content_base64: contentBase64, commit }),
  template: () => api.get('/my-products/template', { responseType: 'blob' }),
};

// Distributors adding shops: existing ones (search) or new ones (invite)
export const shopInviteAPI = {
  search: (q) => api.get('/shop-invites/search', { params: { q } }),
  invite: (data) => api.post('/shop-invites', data),
  list: () => api.get('/shop-invites'),
  resend: (id) => api.post(`/shop-invites/${id}/resend`),
  cancel: (id) => api.delete(`/shop-invites/${id}`),
  lookup: (token) => api.get('/shop-invites/lookup', { params: { token } }),
  myShops: () => api.get('/my-shops'),
  shopProducts: (shopId) => api.get(`/shop-products/${shopId}`),
  setShopProducts: (shopId, productIds) => api.put(`/shop-products/${shopId}`, { product_ids: productIds }),
};

// Farm produce marketplace: farmers sell, offtakers buy (PR 67)
export const produceAPI = {
  mine: () => api.get('/produce/mine'),
  add: (data) => api.post('/produce', data),
  update: (id, data) => api.patch(`/produce/${id}`, data),
  close: (id) => api.delete(`/produce/${id}`),
  market: (params) => api.get('/produce/market', { params }),
  clusters: (q) => api.get('/clusters', { params: q ? { q } : undefined }),
  // Every cluster / association the support team created (wizard list).
  allClusters: () => api.get('/clusters', { params: { include_unmanaged: true } }),
  myClusters: () => api.get('/clusters/mine'),
  chats: () => api.get('/produce/chats'),
  unread: () => api.get('/produce/chats/unread'),
  startChat: (sellerId, body, offer) => api.post('/produce/chats', { seller_id: sellerId, body, offer }),
  messages: (chatId, after) => api.get(`/produce/chats/${chatId}/messages`, { params: after ? { after } : undefined }),
  send: (chatId, body) => api.post(`/produce/chats/${chatId}/messages`, { body }),
  read: (chatId) => api.post(`/produce/chats/${chatId}/read`),
};

// Deal APIs
export const dealAPI = {
  create: (data) => api.post('/deals', data),
  getSellerDeals: (sellerId) => api.get(`/deals/seller/${sellerId}`),
  getBuyerDeals: (buyerId) => api.get(`/deals/buyer/${buyerId}`),
  getActiveDeals: () => api.get('/deals/active/all'),
  update: (dealId, data) => api.put(`/deals/${dealId}`, data),
  delete: (dealId) => api.delete(`/deals/${dealId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: (userType) => api.get(`/subscriptions/plans${userType ? `?user_type=${userType}` : ''}`),
  subscribe: (data) => api.post('/subscriptions/subscribe', data),
  getUserSubscription: (userId) => api.get(`/subscriptions/user/${userId}`),
  cancel: (userId) => api.post(`/subscriptions/cancel/user/${userId}`),
};

// Startbutton: plans priced in USD, paid in naira; receipts in the app.
export const paymentsAPI = {
  config: () => api.get('/payments/config'),
  quote: (planTier) => api.get('/payments/quote', { params: { plan_tier: planTier } }),
  checkout: (planTier, billing = 'month') => api.post('/payments/checkout', { plan_tier: planTier, billing }),
  list: () => api.get('/payments'),
  get: (reference) => api.get(`/payments/${encodeURIComponent(reference)}`),
};

// Stripe checkout APIs
export const stripeAPI = {
  createCheckoutSession: (data) => api.post('/stripe/checkout-session', data),
  verifySession: (sessionId) => api.post(`/stripe/verify-session/${sessionId}`),
  getConfig: () => api.get('/stripe/config'),
};

// Notification APIs
export const notificationAPI = {
  get: (userId) => api.get(`/notifications/user/${userId}`),
  markRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  unreadCount: () => api.get('/notifications/unread-count'),
};

// Government APIs
export const governmentAPI = {
  getFeeds: (feedType, region) => api.get('/government/feeds', { params: { feed_type: feedType, region } }),
  getUpdates: () => api.get('/government-updates'),
};

// Platform APIs
export const platformAPI = {
  getStatistics: () => api.get('/platform/statistics'),
  getUsersByType: (userType) => api.get(`/platform/users/${userType}`),
};

// Who is on African Farm, by type (the photo circles on every home).
// Public-safe fields only: never phone numbers or emails.
export const directoryAPI = {
  list: (userType, params) => api.get(`/directory/${userType}`, { params }),
  get: (userType, userId) => api.get(`/directory/${userType}/${userId}`),
};

// Support chat with the African Farm team (the header's Support button).
// The thread polls `thread(after)` for anything newer than it has.
export const supportAPI = {
  thread: (after) => api.get('/support/thread', { params: after ? { after } : undefined }),
  send: ({ body, image, productId }) => api.post('/support/messages', {
    body: body || null, image: image || null, product_id: productId || null,
  }),
  unread: () => api.get('/support/unread'),
  read: () => api.post('/support/read'),
};

// Home-screen search: products, businesses and government updates in one
// call. Results are scoped to the signed-in user by the backend.
export const searchAPI = {
  search: (q, config = {}) => api.get('/search', { params: { q }, ...config }),
};
